
import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';

interface RegionDotsProps {
  map: mapboxgl.Map;
  regions: Region[];
  markerRefs: mapboxgl.Marker[];
  isGlobalView?: boolean;
}

const RegionDots: React.FC<RegionDotsProps> = ({ map, regions, markerRefs, isGlobalView = false }) => {
  useEffect(() => {
    if (!map) return;
    
    // Clean up existing markers
    markerRefs.forEach(marker => marker.remove());
    markerRefs.length = 0;

    regions.forEach(region => {
      if (!region.coordinates || region.coordinates.length !== 2) {
        console.error(`Invalid coordinates for region: ${region.english_name}`, region.coordinates);
        return;
      }

      // Define region characteristics
      const isLowPresence = region.church_presence?.toLowerCase() === 'low';
      const isUnreached = region.church_presence?.toLowerCase() === 'unreached';
      
      // Determine if this is an area of need (regions with high poverty or that need basic services)
      // In a production app, this would come from real data
      const isAreaOfNeed = region.unreached_population && region.unreached_population > 500000;
      
      // Skip regions with good church presence if not in special need
      if (!isLowPresence && !isUnreached && !isAreaOfNeed) return;

      // Create pin container
      const pin = document.createElement('div');
      
      let className = '';
      let color = '';
      let borderColor = '';
      let scale = '1.2';
      let zIndex = '4';
      
      // Determine pin styling based on region type
      if (isAreaOfNeed) {
        className = 'region-pin region-pin-need pulse-need';
        color = '#9b87f5'; // Purple for areas of need
        borderColor = '#8B5CF6';
        scale = '1.3';
        zIndex = '6';
      } else if (isUnreached) {
        className = 'region-pin region-pin-unreached pulse-unreached';
        color = '#ea384c'; // Red for unreached
        borderColor = '#dc2626';
        scale = '1.4';
        zIndex = '5';
      } else if (isLowPresence) {
        className = 'region-pin region-pin-low';
        color = '#F97316'; // Orange for low presence
        borderColor = '#ea580c';
        scale = '1.2';
        zIndex = '4';
      }
      
      pin.className = className;
      
      // Create SVG pin with fixed size and positioning
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '32');
      svg.setAttribute('height', '32');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z');
      path.setAttribute('fill', color);
      path.setAttribute('stroke', borderColor);
      
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '10');
      circle.setAttribute('r', '3');
      circle.setAttribute('fill', borderColor);
      
      svg.appendChild(path);
      svg.appendChild(circle);
      pin.appendChild(svg);

      // Enhanced styling with absolute positioning for stability
      pin.style.cssText = `
        cursor: pointer;
        z-index: ${zIndex};
        transform: scale(${scale});
        filter: drop-shadow(0 0 6px ${color}40);
        transition: transform 0.3s ease, filter 0.3s ease;
        position: absolute;
      `;

      // Hover effects without changing position
      pin.addEventListener('mouseenter', () => {
        pin.style.transform = `scale(${parseFloat(scale) * 1.2})`;
        pin.style.filter = `drop-shadow(0 0 12px ${color}70)`;
      });

      pin.addEventListener('mouseleave', () => {
        pin.style.transform = `scale(${scale})`;
        pin.style.filter = `drop-shadow(0 0 6px ${color}40)`;
      });
      
      // Popup content based on region type
      let popupContent = '';
      if (isAreaOfNeed) {
        popupContent = `
          <div class="p-3 text-sm bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border-2 border-purple-500">
            <strong class="text-base block mb-2">${region.english_name}</strong>
            <div class="font-medium text-purple-600">Area of Exceptional Need</div>
            <div class="mt-1 text-gray-600">High poverty & lack of basic services</div>
          </div>
        `;
      } else if (isUnreached) {
        popupContent = `
          <div class="p-3 text-sm bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border-2 border-red-500">
            <strong class="text-base block mb-2">${region.english_name}</strong>
            <div class="font-medium text-red-600">Unreached/Very Low Christian Presence</div>
            <div class="mt-1 text-gray-600">Priority area for church planting</div>
          </div>
        `;
      } else {
        popupContent = `
          <div class="p-3 text-sm bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border-2 border-orange-500">
            <strong class="text-base block mb-2">${region.english_name}</strong>
            <div class="font-medium text-orange-600">Low Christian Presence</div>
            <div class="mt-1 text-gray-600">Needs additional support</div>
          </div>
        `;
      }
      
      // Create marker with fixed offset to prevent movement
      const marker = new mapboxgl.Marker({
        element: pin,
        anchor: 'bottom',
        offset: [0, 0]
      })
        .setLngLat(region.coordinates)
        .setPopup(
          new mapboxgl.Popup({ 
            offset: [0, -15],
            closeButton: true,
            closeOnClick: true,
            maxWidth: '240px',
            anchor: 'bottom'
          })
            .setHTML(popupContent)
        )
        .addTo(map);

      markerRefs.push(marker);
    });

    return () => {
      markerRefs.forEach(marker => marker.remove());
      markerRefs.length = 0;
    };
  }, [map, regions, markerRefs, isGlobalView]);

  return null;
};

export default RegionDots;
